import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw, Hash, Plus, X } from 'lucide-react';
import { API, useAuth } from '../App';
import { useT } from '../i18n';
import XpnsrPageHeader from './XpnsrPageHeader';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { normalizeParamKeys, paramKeyLabel } from '../reportKeys';

const MAX_LEVELS = 3;

export default function SubIdReports() {
  const { user } = useAuth();
  const { t } = useT();

  // Grouping levels: sub1, then (optionally) a second and third key inside it.
  const [levels, setLevels] = useState(['sub1']);
  const [days, setDays] = useState('30');
  const [campaignId, setCampaignId] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paramKeys, setParamKeys] = useState(normalizeParamKeys(null));

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/campaigns`).then((r) => setCampaigns(r.data || [])).catch(() => {});
  }, [user]);

  // Grouping keys: sub1..sub30 plus custom keys declared in the campaign(s).
  useEffect(() => {
    if (!user) return;
    const params = campaignId !== 'all' ? { campaign_id: campaignId } : {};
    axios
      .get(`${API}/analytics/param-keys`, { params })
      .then((r) => {
        const keys = normalizeParamKeys(r.data);
        setParamKeys(keys);
        setLevels((prev) => {
          const kept = prev.filter((k) => keys.some((x) => x.key === k));
          return kept.length ? kept : ['sub1'];
        });
      })
      .catch(() => setParamKeys(normalizeParamKeys(null)));
  }, [user, campaignId]);

  const groupBy = levels.join(',');

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = { days, group_by: groupBy };
      if (campaignId !== 'all') params.campaign_id = campaignId;
      const res = await axios.get(`${API}/analytics/sub-ids`, { params });
      setData(res.data || []);
    } catch (err) {
      toast.error(t('Failed to load sub-ID report'));
    } finally {
      setLoading(false);
    }
  }, [user, days, groupBy, campaignId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setLevel = (i, key) => setLevels((prev) => prev.map((k, j) => (j === i ? key : k)));
  const removeLevel = (i) => setLevels((prev) => prev.filter((_, j) => j !== i));
  const addLevel = () => {
    const next = paramKeys.find((k) => !levels.includes(k.key));
    if (next) setLevels((prev) => [...prev, next.key]);
  };

  // Nested view: rows of the same first-level value stay together, the
  // biggest groups first; a repeated parent value is dimmed.
  const rows = useMemo(() => {
    const list = data.map((r) => ({ ...r, values: Array.isArray(r.values) ? r.values : [r.value] }));
    if (levels.length < 2) return list;
    const totals = {};
    list.forEach((r) => {
      const k = String(r.values[0]);
      totals[k] = (totals[k] || 0) + (r.clicks || 0);
    });
    return list.sort((a, b) => {
      const ka = String(a.values[0]);
      const kb = String(b.values[0]);
      if (ka !== kb) return (totals[kb] || 0) - (totals[ka] || 0) || ka.localeCompare(kb);
      return (b.clicks || 0) - (a.clicks || 0);
    });
  }, [data, levels.length]);

  const exportCSV = () => {
    if (rows.length === 0) return;
    const esc = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [...levels, 'Clicks', 'Unique', 'Conversions', 'Revenue', 'CR%'].join(',');
    const body = rows
      .map((r) =>
        [...levels.map((_, i) => r.values[i] ?? ''), r.clicks, r.unique_clicks, r.conversions, r.revenue, r.cr]
          .map(esc)
          .join(','),
      )
      .join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sub-id-report-${levels.join('-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('CSV exported'));
  };

  const title = levels.join(' → ');

  return (
    <div className="px-8 pt-2 pb-8 space-y-6" data-testid="sub-id-reports">
      <XpnsrPageHeader
        kicker={t('Reports')}
        title={t('Sub-ID drill-down')}
        subtitle={t('Break down clicks and conversions by sub1–sub30 or any parameter declared in a campaign')}
        actions={
          <>
            <button type="button" className="xp-btn" onClick={fetchData} data-testid="subid-refresh-btn">
              <RefreshCw style={{ width: 14, height: 14 }} />
              {t('Refresh')}
            </button>
            <button
              type="button"
              className="xp-btn"
              onClick={exportCSV}
              disabled={rows.length === 0}
              data-testid="subid-export-btn"
            >
              <Download style={{ width: 14, height: 14 }} />
              {t('Export CSV')}
            </button>
          </>
        }
      />

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-sm whitespace-nowrap">{t('Group by:')}</Label>
              {levels.map((key, i) => (
                <div key={i} className="flex items-center gap-1">
                  {i > 0 ? <span className="text-muted-foreground px-1" aria-hidden="true">→</span> : null}
                  <Select value={key} onValueChange={(v) => setLevel(i, v)}>
                    <SelectTrigger
                      className="w-40"
                      data-testid={i === 0 ? 'subid-groupby-select' : `subid-groupby-select-${i}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {paramKeys
                        .filter((k) => k.key === key || !levels.includes(k.key))
                        .map((k) => (
                          <SelectItem key={k.key} value={k.key}>
                            {paramKeyLabel(k)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {i > 0 ? (
                    <button
                      type="button"
                      className="xp-icon-btn"
                      onClick={() => removeLevel(i)}
                      aria-label={t('Remove level')}
                      title={t('Remove level')}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </div>
              ))}
              {levels.length < MAX_LEVELS ? (
                <button type="button" className="xp-btn xp-btn-sm" onClick={addLevel} data-testid="subid-add-level">
                  <Plus style={{ width: 14, height: 14 }} />
                  {t('Add level')}
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">{t('Period:')}</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-32" data-testid="subid-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('Today')}</SelectItem>
                  <SelectItem value="7">{t('7 days')}</SelectItem>
                  <SelectItem value="14">{t('14 days')}</SelectItem>
                  <SelectItem value="30">{t('30 days')}</SelectItem>
                  <SelectItem value="90">{t('90 days')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">{t('Campaign:')}</Label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="w-44" data-testid="subid-campaign-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('All campaigns')}</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader style={{ padding: '16px 22px' }}>
          <CardTitle className="xp-panel-title">
            <Hash className="w-4 h-4" />
            {t('{key} breakdown', { key: title })}
          </CardTitle>
        </CardHeader>
        <CardContent style={{ padding: '0 22px 22px' }}>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="skeleton h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Hash className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{t('No data found for {key}', { key: title })}</p>
              <p className="text-xs mt-1">{t('Make sure your tracking URLs include {key}=... parameter', { key: levels[0] })}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="xp-ctable xp-subid-table" data-testid="subid-table">
                <thead>
                  <tr>
                    {levels.map((k) => (
                      <th key={k}>{k}</th>
                    ))}
                    <th className="num">{t('Clicks')}</th>
                    <th className="num" title={t('Unique clicks: a visitor counts once within the uniqueness window of the campaign.')}>{t('Unique')}</th>
                    <th className="num">{t('Conversions')}</th>
                    <th className="num">{t('Revenue')}</th>
                    <th className="num" title={t('CR % = conversions ÷ clicks × 100')}>{t('CR %')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const prev = rows[idx - 1];
                    return (
                      <tr key={levels.map((_, i) => String(row.values[i])).join('|') + ':' + idx}>
                        {levels.map((_, i) => {
                          const v = row.values[i];
                          const repeat =
                            i < levels.length - 1 && prev && levels.slice(0, i + 1).every((__, j) => prev.values[j] === row.values[j]);
                          return (
                            <td key={i} className={'sub-v' + (repeat ? ' repeat' : '') + (v == null ? ' none' : '')}>
                              {v == null ? '—' : String(v)}
                            </td>
                          );
                        })}
                        <td className="num">{(row.clicks || 0).toLocaleString()}</td>
                        <td className="num">{(row.unique_clicks || 0).toLocaleString()}</td>
                        <td className="num">{(row.conversions || 0).toLocaleString()}</td>
                        <td className="num">${(row.revenue || 0).toLocaleString()}</td>
                        <td className={'num' + (row.cr > 0 ? ' pos' : ' dim')}>{row.cr}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
